import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  Slide,
  Typography,
  useTheme
} from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions/transition';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import InfoIcon from '@material-ui/icons/Info';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import React, {
  forwardRef,
  ReactElement,
  Ref,
  useEffect,
  useState
} from 'react';
import { FeedbackContent } from '../../hooks/feedback';
import useStyles from './styles';

interface FeedbackDialogProps {
  message: FeedbackContent;
}

interface FeedbackTypeContent {
  backgroundColor: string;
  icon: any;
}

export type FeedbackType = 'success' | 'error' | 'info' | 'warning' | 'default';

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children?: ReactElement<any, any> },
  ref: Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({ message }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const FEEDBACK_BY_TYPE: { [key: string]: FeedbackTypeContent } = {
    error: {
      backgroundColor: theme.palette.error.main,
      icon: <HighlightOffIcon />,
    },
    info: { backgroundColor: theme.palette.info.main, icon: <InfoIcon /> },
    warning: {
      backgroundColor: theme.palette.warning.main,
      icon: <ReportProblemIcon />,
    },
    success: {
      backgroundColor: theme.palette.success.main,
      icon: <CheckCircleOutlineIcon />,
    },
    default: {
      backgroundColor: '#fff',
      icon: <CircularProgress color="primary" />,
    },
  };

  const getDefaultFeedbackTitle = (feedbackType: FeedbackType) => {
    switch (feedbackType) {
      case 'success':
        return 'Sucesso!';
      case 'info':
        return 'Informativo!';
      case 'error':
        return 'Oops!';
      case 'warning':
        return 'Alerta!';

      default:
        return '';
    }
  };

  const getFeedbackStyle = (type: FeedbackType): FeedbackTypeContent => {
    return FEEDBACK_BY_TYPE[message?.loading ? 'default' : type];
  };

  const handleDialogClose = () => {
    if (message.onClose) {
      message.onClose();
    }
    setOpen(false);
  };

  useEffect(() => {
    if (message.loading) {
      setOpen(true);
    }
    if (message.description && !message.loading) {
      setOpen(true);
      if (message.closeTimeout) {
        const timer = setTimeout(() => {
          handleDialogClose();
        }, message.closeTimeout);

        return () => {
          clearTimeout(timer);
        };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message]);

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      maxWidth="xs"
      fullWidth
    >
      <DialogContent
        className={classes.dialogContent}
        style={{
          backgroundColor: `${
            getFeedbackStyle(message.type!)?.backgroundColor
          }`,
        }}
      >
        <Grid container className={classes.dialogContentContainer}>
          {getFeedbackStyle(message.type!)?.icon}
          {message.loading ? (
            <Typography className={classes.typographyDefault}>
              Aguarde...
            </Typography>
          ) : (
            <>
              <Typography className={classes.dialogTitleText}>
                {message.title || getDefaultFeedbackTitle(message?.type)}
              </Typography>

              <Typography className={classes.dialogDescription}>
                {message?.description}
              </Typography>
            </>
          )}

          {!message.loading && (
            <Button
              onClick={handleDialogClose}
              className={classes.feedbackDialogButton}
              variant="contained"
            >
              Fechar
            </Button>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
